import { useEffect, useRef, useState } from "react";
import { Bell, ArrowDownToLine, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const ago = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function NotificationBell() {
  const { refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const seen = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications?limit=20");
        if (!alive) return;
        if (seen.current) {
          const fresh = data.items.filter((n) => !seen.current.has(n.id));
          fresh.forEach((n) => toast.success(n.title, { description: n.body, duration: 8000, position: "bottom-right" }));
          if (fresh.length) refresh();
        }
        seen.current = new Set(data.items.map((n) => n.id));
        setItems(data.items);
        setUnread(data.unread);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [refresh]);

  const markRead = async () => {
    if (!unread) return;
    await api.post("/notifications/read").catch((e) => console.warn("Mark-read failed", e));
    setUnread(0);
    setItems((it) => it.map((n) => ({ ...n, read: true })));
  };

  return (
    <DropdownMenu onOpenChange={(o) => o && markRead()}>
      <DropdownMenuTrigger asChild>
        <button className="avatar-btn relative" data-testid="notifications-bell" aria-label="Notifications">
          <Bell size={16} className="text-zinc-300" />
          {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notifications-unread">{unread}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#141416] border-white/10 text-white w-[340px] p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Notifications</span>
          <span className="text-[10px] text-zinc-500 flex items-center gap-1"><CheckCheck size={12} /> all read</span>
        </div>
        <div className="max-h-[360px] overflow-y-auto sidebar-scroll">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-zinc-500" data-testid="notifications-empty">No notifications yet. Deposit alerts land here.</div>
          ) : items.map((n) => (
            <div key={n.id} className={`flex gap-3 px-4 py-3 border-b border-white/[0.04] ${n.read ? "" : "bg-amber-500/5"}`} data-testid={`notification-${n.id}`}>
              <span className="w-8 h-8 rounded-lg bg-emerald-500/15 text-emerald-400 flex items-center justify-center shrink-0"><ArrowDownToLine size={14} /></span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">{n.title}</div>
                <div className="text-xs text-zinc-400 leading-snug">{n.body}</div>
                <div className="text-[10px] text-zinc-600 mt-1 font-mono-num">{ago(n.created_at)}</div>
              </div>
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
